import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import type { BillingCycle } from './BillingCycleSelector'

interface DailyUsageData {
  date: string
  events: number
  cost: number
}

interface UsageMetricsTableProps {
  billingCycle: BillingCycle
  usageData?: DailyUsageData[]
}

export function UsageMetricsTable({ usageData = [] }: UsageMetricsTableProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  // Generate last 7 days data from today
  const generateLast7Days = (): DailyUsageData[] => {
    const days: DailyUsageData[] = []
    const today = new Date()

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(today.getDate() - i)
      const dateString = date.toISOString().split('T')[0]

      // Mock data - in real app this would come from API
      const mockEvents =
        i === 0
          ? 1181
          : i === 1
            ? 1879
            : i === 2
              ? 551
              : i === 3
                ? 0
                : i === 4
                  ? 0
                  : i === 5
                    ? 0
                    : 3

      days.push({
        date: dateString,
        events: mockEvents,
        cost: 0.0,
      })
    }

    return days
  }

  const mockUsageData = generateLast7Days()

  const displayData = usageData.length > 0 ? usageData : mockUsageData
  const totalEvents = displayData.reduce((sum, day) => sum + day.events, 0)
  const totalCost = displayData.reduce((sum, day) => sum + day.cost, 0)

  const dates = displayData.map(d => formatDate(d.date))

  return (
    <div className='space-y-6'>
      {/* Summary Cards */}
      <div className='grid gap-4 md:grid-cols-2'>
        <Card className='bg-transparent border'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Total Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {totalEvents.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className='bg-transparent border'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Total Spend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {formatCurrency(totalCost)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Tables */}
      <div className='space-y-6'>
        {/* Daily usage by product */}
        <Card className='bg-transparent border'>
          <CardHeader>
            <CardTitle className='text-lg'>Daily usage by product</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-2 px-2 font-medium'>
                      <div className='flex items-center gap-2'>EVENTS</div>
                    </th>
                    <th className='text-left py-2 px-2 font-medium'>TOTAL</th>
                    {dates.map((date, index) => (
                      <th
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={index}
                        className='text-left py-2 px-2 font-medium text-sm'
                      >
                        {date.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className='border-b'>
                    <td className='py-2 px-2'>
                      <div className='flex items-center gap-2'>
                        <span className='text-orange-600'>●</span>
                        Signals
                      </div>
                    </td>
                    <td className='py-2 px-2 font-medium'>
                      {totalEvents.toLocaleString()}
                    </td>
                    {displayData.map((day, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      <td key={index} className='py-2 px-2'>
                        {day.events.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Daily spend by product */}
        <Card className='bg-transparent border'>
          <CardHeader>
            <CardTitle className='text-lg'>Daily spend by product</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-2 px-2 font-medium'>
                      <div className='flex items-center gap-2'>EVENTS</div>
                    </th>
                    <th className='text-left py-2 px-2 font-medium'>
                      TOTAL SPEND
                    </th>
                    {dates.map((date, index) => (
                      <th
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={index}
                        className='text-left py-2 px-2 font-medium text-sm'
                      >
                        {date.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className='border-b'>
                    <td className='py-2 px-2'>
                      <div className='flex items-center gap-2'>
                        <span className='text-blue-600'>●</span>
                        Signals
                      </div>
                    </td>
                    <td className='py-2 px-2 font-medium'>
                      {formatCurrency(totalCost)}
                    </td>
                    {displayData.map((day, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      <td key={index} className='py-2 px-2'>
                        {formatCurrency(day.cost)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
